
import React, { useState, useCallback, useEffect } from 'react';
import { processExamFile } from '../services/processingService';
import type { ProcessedProblem, ExplanationGuidelines, BaseProblem } from '../types';
import Spinner from './Spinner';
import LatexRenderer from './LatexRenderer';
import ProblemBodyRenderer from './ProblemBodyRenderer';
import InlineEditableContent from './InlineEditableContent';
import LatexEditorPopup from './LatexEditorPopup';
import toast from 'react-hot-toast';


interface ExamRecognitionViewProps {
    guidelines: ExplanationGuidelines;
    onAddMultipleBaseProblems: (problems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[]) => void;
    allFolderPaths: string[];
}

const CopyButton: React.FC<{ textToCopy: string; label: string }> = ({ textToCopy, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!textToCopy) return;

        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            toast.error('클립보드 복사에 실패했습니다.');
        });
    };

    const buttonClass = `text-xs px-2 py-1 rounded-md transition-colors duration-200 ${copied ? 'bg-green-600 text-white' : 'bg-[var(--color-button-secondary-bg)] hover:bg-[var(--color-button-secondary-hover)] text-white'}`;

    return (
        <button onClick={handleCopy} className={buttonClass}>
            {copied ? '복사 완료!' : label}
        </button>
    );
};

const PROCESSING_STEPS = [
    { id: 'file', label: '파일 변환' },
    { id: 'classify', label: '페이지 분류' },
    { id: 'extract_problem', label: '문제 추출 및 OCR' },
    { id: 'extract_explanation', label: '해설 추출' },
    { id: 'match', label: '데이터 매칭' },
    { id: 'save', label: '저장' }
];

const ProcessingSteps: React.FC<{ currentMessage: string }> = ({ currentMessage }) => {
    // Helper to find the active step index based on the message
    const getActiveStepIndex = (msg: string) => {
        if (!msg) return -1;
        if (msg.includes('완료')) return PROCESSING_STEPS.length;
        if (msg.includes('파일 변환')) return 0;
        if (msg.includes('분류')) return 1;
        if (msg.includes('문제 추출')) return 2;
        if (msg.includes('해설 추출')) return 3;
        if (msg.includes('매칭')) return 4;
        if (msg.includes('저장')) return 5;
        return 0;
    };

    const activeIndex = getActiveStepIndex(currentMessage);

    return (
        <div className="w-full max-w-md mx-auto my-8 p-6 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl shadow-lg">
            <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-4 text-center">
                {currentMessage || '준비 중...'}
            </h3>
            <div className="space-y-3">
                {PROCESSING_STEPS.map((step, index) => {
                    const isActive = index === activeIndex;
                    const isCompleted = index < activeIndex;
                    const isPending = index > activeIndex;

                    return (
                        <div 
                            key={step.id} 
                            className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-300 ${
                                isActive ? 'bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30 translate-x-2 shadow-md' : 
                                isCompleted ? 'bg-green-500/5' : 'opacity-40'
                            }`}
                        >
                            <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
                                isActive ? 'bg-[var(--color-accent)] text-white animate-pulse' : 
                                isCompleted ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'
                            }`}>
                                {isCompleted ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <span className="text-xs font-bold">{index + 1}</span>
                                )}
                            </div>
                            <span className={`font-medium ${
                                isActive ? 'text-[var(--color-accent)] font-bold' : 
                                isCompleted ? 'text-green-400' : 'text-[var(--color-text-secondary)]'
                            }`}>
                                {step.label}
                            </span>
                            {isActive && <div className="ml-auto"><Spinner small /></div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ExamRecognitionView: React.FC<ExamRecognitionViewProps> = ({ guidelines, onAddMultipleBaseProblems, allFolderPaths }) => {
    const [file, setFile] = useState<File | null>(null);
    const [processedProblems, setProcessedProblems] = useState<ProcessedProblem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [stageMessage, setStageMessage] = useState('');
    const [isTypesetting, setIsTypesetting] = useState(false);

    const [selectedToSave, setSelectedToSave] = useState<Set<string>>(new Set());
    const [saveFolder, setSaveFolder] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [latexEditorState, setLatexEditorState] = useState<{ isOpen: boolean; initialValue: string; onSave: (newValue: string) => void; }>({ isOpen: false, initialValue: '', onSave: () => {} });

    useEffect(() => {
        if (processedProblems.length > 0 && (window as any).MathJax) {
            setIsTypesetting(true);
            const timeoutId = setTimeout(() => {
                (window as any).MathJax.typesetPromise()
                    .catch((err: any) => console.error("ExamRecognitionView MathJax Error:", err))
                    .finally(() => setIsTypesetting(false));
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [processedProblems]);

    const resetState = () => {
        setFile(null);
        setProcessedProblems([]);
        setIsLoading(false);
        setError(null);
        setStageMessage('');
        setSelectedToSave(new Set());
        setSaveFolder('');
        const fileInput = document.getElementById('exam-file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            resetState();
            setFile(selectedFile);
        }
    };

    const handleProgress = (message: string, problems: ProcessedProblem[]) => {
        setStageMessage(message);
        setProcessedProblems([...problems]);
    };

    const handleStartProcessing = useCallback(async () => {
        if (!file) return;

        setIsLoading(true);
        setError(null);
        setStageMessage('준비 중...');
        setProcessedProblems([]);

        try {
            await processExamFile(file, guidelines, handleProgress);
        } catch (err: any) {
            const errorMessage = err.message || "알 수 없는 오류가 발생했습니다.";
            setError(errorMessage);
            setStageMessage(`오류: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    }, [file, guidelines]);
    
    const handleProblemContentUpdate = (problemId: string, field: 'ocrText' | 'choices' | 'explanation', newValue: string) => {
        setProcessedProblems(prev => prev.map(p => {
            if (p.id === problemId) {
                return { ...p, [field]: newValue };
            }
            return p;
        }));
    };
    
    const handleMathDoubleClick = useCallback((initialValue: string, onSave: (newValue: string) => void) => {
        setLatexEditorState({ isOpen: true, initialValue, onSave });
    }, []);

    const handleToggleSelect = (problemId: string) => {
        setSelectedToSave(prev => {
            const newSet = new Set(prev);
            if (newSet.has(problemId)) newSet.delete(problemId);
            else newSet.add(problemId);
            return newSet;
        });
    };

    const handleSaveToLibrary = async () => {
        if (selectedToSave.size === 0) return;
        setIsSaving(true);
        try {
            const problemsToSave: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[] = [];
            processedProblems.forEach(p => {
                if (selectedToSave.has(p.id)) {
                    problemsToSave.push({
                        parts: {
                            lines: p.ocrText?.split('\n') || [],
                            type: p.choices ? '객관식' : '주관식',
                            choices: p.choices || null,
                        },
                        createdAt: new Date().toISOString(),
                        explanation: p.explanation || null,
                        croppedImage: p.croppedImageBase64,
                        problemNumber: p.problemNumber,
                        folder: saveFolder.trim() || undefined,
                        source: file ? file.name : '미분류',
                        subject: p.subject || null,
                        majorUnit: p.majorUnit || null,
                        subUnit: p.subUnit || null,
                        originalFolder: null,
                        coreIdeas: p.coreIdeas || [],
                        metadataStatus: 'completed',
                        difficultyScore: 5, // Default score
                    });
                }
            });
            await onAddMultipleBaseProblems(problemsToSave);
            toast.success(`${problemsToSave.length}개의 문제가 라이브러리에 저장되었습니다.`);
            setSelectedToSave(new Set());
        } catch (e) {
            console.error("Failed to save to library:", e);
            toast.error("저장에 실패했습니다.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const isProcessing = !!stageMessage && !stageMessage.startsWith('모든 작업 완료') && !stageMessage.startsWith('오류') && !stageMessage.startsWith('치명적');

    return (
        <div className="space-y-8">
            <h2 className="text-2xl font-bold text-[var(--color-accent)]">시험지 인식 및 해설 생성</h2>

            {/* 1. File Upload */}
            <div className="bg-[var(--color-bg-secondary)] p-6 rounded-lg shadow-inner">
                <label htmlFor="exam-file-upload" className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">시험지 파일 업로드 (PDF/이미지)</label>
                <input
                    id="exam-file-upload"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    className="w-full text-sm text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-accent)] file:text-[var(--color-accent-text)] hover:file:bg-[var(--color-accent-hover)]"
                />
                {file && <p className="mt-2 text-sm text-[var(--color-text-secondary)]">선택된 파일: {file.name}</p>}
                <div className="mt-4">
                    <button
                        onClick={handleStartProcessing}
                        disabled={!file || isProcessing}
                        className="w-full sm:w-auto bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-[var(--color-accent-text)] font-bold py-2 px-4 rounded-lg transition-colors duration-300 disabled:bg-[var(--color-text-tertiary)] disabled:cursor-not-allowed"
                    >
                        {isProcessing ? '분석 중...' : '분석 시작'}
                    </button>
                    {(isProcessing || processedProblems.length > 0) && (
                         <button
                            onClick={resetState}
                            className="ml-4 w-full sm:w-auto bg-[var(--color-button-secondary-bg)] hover:bg-[var(--color-button-secondary-hover)] text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300"
                        >
                           새로운 파일로 시작
                        </button>
                    )}
                </div>
            </div>

            {(isProcessing || processedProblems.length > 0 || error) && (
                <div className="space-y-6">
                    <h3 className="text-xl font-bold text-[var(--color-text-primary)] border-b border-[var(--color-border)] pb-2">분석 결과</h3>
                    
                    {isProcessing && (
                        <ProcessingSteps currentMessage={stageMessage} />
                    )}
                    
                    {error && <p className="text-red-400 mt-2 text-sm">{error}</p>}
                    
                    {!isProcessing && processedProblems.length > 0 && (
                        <>
                            <div className="sticky top-[65px] bg-[var(--color-bg-secondary)] p-4 rounded-lg shadow-md z-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <span className="font-bold text-lg">{selectedToSave.size}개 문제 선택됨</span>
                                <div className="flex items-center gap-2">
                                     <input 
                                        type="text"
                                        value={saveFolder}
                                        onChange={(e) => setSaveFolder(e.target.value)}
                                        placeholder="폴더 이름 (예: 24년 수능)"
                                        className="w-full sm:w-64 bg-black/20 border border-[var(--color-border-glass)] rounded-md py-2 px-3 text-[var(--color-text-primary)]"
                                    />
                                    <button onClick={handleSaveToLibrary} disabled={selectedToSave.size === 0 || isSaving} className="btn-success px-4 py-2 rounded-md font-bold disabled:opacity-50">
                                        {isSaving ? '저장 중...' : '라이브러리에 저장'}
                                    </button>
                                </div>
                            </div>

                            {isTypesetting && (
                                <div className="flex justify-center py-16"><Spinner message="수식을 렌더링하고 있습니다..." /></div>
                            )}
                            <div className={`flex flex-col gap-8 ${isTypesetting ? 'opacity-0' : 'opacity-100 transition-opacity'}`}>
                                {processedProblems.map(problem => (
                                    <div key={problem.id} className={`bg-[var(--color-bg-secondary)] p-6 rounded-lg shadow transition-all ${selectedToSave.has(problem.id) ? 'ring-2 ring-[var(--color-accent)]' : ''}`}>
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="flex flex-col">
                                                 <h4 className="font-bold text-[var(--color-text-primary)] text-xl">문제 #{problem.problemNumber}</h4>
                                                 <div className="text-xs text-[var(--color-text-secondary)] mt-1">
                                                     {problem.subject && <span className="mr-2">[{problem.subject}]</span>}
                                                     {problem.majorUnit && <span className="mr-2">{problem.majorUnit}</span>}
                                                     {problem.subUnit && <span>&gt; {problem.subUnit}</span>}
                                                 </div>
                                                 {problem.coreIdeas && problem.coreIdeas.length > 0 && (
                                                     <div className="flex gap-2 mt-1 flex-wrap">
                                                         {problem.coreIdeas.map((idea, idx) => (
                                                             <span key={idx} className="text-[10px] bg-blue-900/30 text-blue-300 px-1.5 py-0.5 rounded border border-blue-800">#{idea}</span>
                                                         ))}
                                                     </div>
                                                 )}
                                            </div>
                                            <div className="flex items-center">
                                                <label className="flex items-center cursor-pointer">
                                                    <input type="checkbox" checked={selectedToSave.has(problem.id)} onChange={() => handleToggleSelect(problem.id)} className="h-5 w-5 rounded accent-[var(--color-accent)]" />
                                                    <span className="ml-2 font-semibold">라이브러리에 저장</span>
                                                </label>
                                            </div>
                                        </div>
                                        
                                        {/* Row 1: Image + OCR Text */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                             <div className="p-2 bg-[var(--color-bg-primary)] rounded-lg border border-[var(--color-border)] h-full min-h-[300px] flex items-center justify-center overflow-hidden">
                                                    {problem.croppedImageBase64 ? (
                                                        <img src={`data:image/jpeg;base64,${problem.croppedImageBase64}`} alt={`Problem ${problem.problemNumber}`} className="max-w-full max-h-[500px] object-contain rounded" />
                                                    ) : (
                                                        <div className="text-xs text-[var(--color-text-tertiary)]">이미지 없음</div>
                                                    )}
                                             </div>
                                             
                                             <div className="flex flex-col h-full min-h-[300px] gap-4">
                                                 {/* Question Text */}
                                                 <div className="flex-1">
                                                     <div className="flex justify-between items-center mb-2">
                                                         <p className="text-sm font-medium text-[var(--color-text-secondary)]">인식된 문제 (텍스트)</p>
                                                     </div>
                                                     <div className="p-4 sketchbook rounded-lg overflow-y-auto border border-[var(--color-sketchbook-border)] h-[200px]">
                                                            {problem.status === 'ocr' || problem.ocrText ? (
                                                                <InlineEditableContent 
                                                                    content={problem.ocrText || ''} 
                                                                    onContentChange={(v) => handleProblemContentUpdate(problem.id, 'ocrText', v)} 
                                                                    onMathDoubleClick={handleMathDoubleClick} 
                                                                />
                                                            ) : (
                                                                <Spinner small message="텍스트 인식 중..." />
                                                            )}
                                                     </div>
                                                 </div>
                                                 
                                                 {/* Choices (Separate Field) */}
                                                 <div className="flex-1">
                                                      <div className="flex justify-between items-center mb-2">
                                                         <p className="text-sm font-medium text-[var(--color-text-secondary)]">객관식 선지 (OCR 결과)</p>
                                                     </div>
                                                     <div className="p-4 bg-[var(--color-sketchbook-alt-bg)] text-[var(--color-sketchbook-text)] rounded-lg overflow-y-auto border border-[var(--color-sketchbook-alt-border)] h-[100px]">
                                                            <InlineEditableContent 
                                                                content={problem.choices || ''} 
                                                                onContentChange={(v) => handleProblemContentUpdate(problem.id, 'choices', v)} 
                                                                onMathDoubleClick={handleMathDoubleClick} 
                                                            />
                                                     </div>
                                                 </div>
                                             </div>
                                        </div>

                                        {/* Row 2: Explanation */}
                                        <div className="flex flex-col w-full">
                                            <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">생성된 해설</p>
                                            <div className="p-4 sketchbook rounded-lg overflow-y-auto border border-[var(--color-sketchbook-border)] min-h-[150px]">
                                                {problem.explanation ? (
                                                    <InlineEditableContent 
                                                        content={problem.explanation} 
                                                        onContentChange={(v) => handleProblemContentUpdate(problem.id, 'explanation', v)} 
                                                        onMathDoubleClick={handleMathDoubleClick} 
                                                    />
                                                ) : (
                                                    <div className="flex items-center justify-center h-full text-xs text-[var(--color-text-tertiary)] py-8">
                                                        {problem.status === 'failed' && <span className="text-red-400">{problem.error}</span>}
                                                        {problem.status !== 'failed' && problem.status !== 'completed' && <Spinner small message="해설 생성 중..." />}
                                                        {problem.status === 'completed' && !problem.explanation && '해설이 생성되지 않았습니다.'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            {latexEditorState.isOpen && (
                <LatexEditorPopup
                    initialValue={latexEditorState.initialValue}
                    onSave={(newValue) => {
                        latexEditorState.onSave(newValue);
                        setLatexEditorState({ isOpen: false, initialValue: '', onSave: () => {} });
                    }}
                    onClose={() => setLatexEditorState({ isOpen: false, initialValue: '', onSave: () => {} })}
                />
            )}
        </div>
    );
};

export default ExamRecognitionView;